import { clsx } from '@alexis/helpers/clsx';
import { genzoTrackActionEvent } from '@alexis/helpers/genzo';
import cuid from 'cuid';
import { useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { NavLink, useLocation } from 'react-router-dom';

import FlightSearchForm from '@components/flights/FlightSearchForm';
import HotelSearchForm from '@components/hotels/HotelSearchForm';

import { flightsRoute, hotelsRoute } from '@helpers/routeTranslation';

import ProductFlightsIcon from '@icons/product-flights.svg';
import ProductHotelsIcon from '@icons/product-hotels.svg';
import ProductTrainIcon from '@icons/product-train.svg';

import useLocaleParam from '@hooks/useLocaleParam';

import { getCurrentSiteState, getTranslationsState } from '@redux/selectors';

import styles from './SearchForm.module.scss';

export type SearchFormProps = {
  pageType: string;
  availablePaymentMethods: HomepageFlightsPaymentMethod[];
  geolocationCoordinates?: GeolocationCoordinates;
  nearestCityByIP?: Place;
  wegoAnalyticsClientId?: string;
  wegoAnalyticsClientSessionId?: string;
  pageViewId: string;
  onPreferredPaymentMethodsChange: (paymentMethods: number[]) => void;
};

const SearchForm: React.FC<SearchFormProps> = ({
  pageType,
  availablePaymentMethods,
  geolocationCoordinates,
  nearestCityByIP,
  wegoAnalyticsClientId,
  wegoAnalyticsClientSessionId,
  pageViewId,
  onPreferredPaymentMethodsChange,
}) => {
  const tabsRef = useRef<HTMLDivElement>(null);
  const localeParam = useLocaleParam();

  const { search, state } = useLocation();

  const currentSite = useSelector(getCurrentSiteState);
  const translations = useSelector(getTranslationsState);

  const searchWithoutExtraParams = useMemo<string>(() => {
    const searchParams = new URLSearchParams(search);

    searchParams.delete('open_calendar');

    const updatedSearchParams = searchParams.toString();

    return !!updatedSearchParams ? `?${updatedSearchParams}` : '';
  }, [search]);

  const trainsGenzoActionId = cuid();
  const getTabIndex = (target: EventTarget) => {
    const tabs = tabsRef.current?.getElementsByClassName(styles.tab) ?? [];
    return Array.from(tabs).indexOf((target as Element).closest(`.${styles.tab}`) as Element);
  };

  const handleOnProductsClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    eventData: {
      id?: string;
      category?: string;
      object?: string;
      action?: string;
      value: string;
    },
    genzoActionId?: string,
  ) => {
    if (!!wegoAnalyticsClientId && !!wegoAnalyticsClientSessionId) {
      genzoTrackActionEvent(
        API_BASE_URL,
        wegoAnalyticsClientId,
        wegoAnalyticsClientSessionId,
        undefined,
        {
          id: eventData.id || pageViewId,
          category: eventData.category || 'products',
          object: eventData.object || 'tab',
          action: eventData.action || (getTabIndex(e.target) + 1).toString(),
          value: eventData.value,
        },
        genzoActionId,
      );
    }
  };

  return (
    <div className={clsx(styles.searchForm, styles.formMinHeight)}>
      <div ref={tabsRef} className={styles.tabs}>
        <div className={styles.productTabs}>
          <NavLink
            className={({ isActive }) =>
              clsx(styles.tab, (pageType === 'home' || isActive) && styles.active)
            }
            to={{
              pathname: flightsRoute(currentSite, localeParam),
              search: searchWithoutExtraParams,
            }}
            state={state}
            onClick={(e) =>
              handleOnProductsClick(e, {
                value: 'flights',
              })
            }
          >
            <ProductFlightsIcon className={styles.iconSvg} />
            {translations.flights}
          </NavLink>

          <NavLink
            className={({ isActive }) => clsx(styles.tab, isActive && styles.active)}
            to={{
              pathname: hotelsRoute(currentSite, localeParam),
              search: searchWithoutExtraParams,
            }}
            state={state}
            onClick={(e) =>
              handleOnProductsClick(e, {
                value: 'hotels',
              })
            }
          >
            <ProductHotelsIcon className={styles.iconSvg} />
            {translations.hotels}
          </NavLink>

          {currentSite.countryCode === 'IN' ? (
            <a
              className={styles.tab}
              href={`https://trains.wego.com?wego_click_id=${trainsGenzoActionId}`}
              target='_blank'
              rel='noreferrer nofollow'
              onClick={(e) =>
                handleOnProductsClick(
                  e,
                  {
                    value: 'trains',
                  },
                  trainsGenzoActionId,
                )
              }
            >
              <ProductTrainIcon className={styles.iconSvg} />
              {translations.wego_trains}
            </a>
          ) : null}
        </div>
      </div>

      <div className={styles.tabContent}>
        {/^(?:home|flights)$/i.test(pageType) ? (
          <FlightSearchForm
            geolocationCoordinates={geolocationCoordinates}
            nearestCityByIP={nearestCityByIP}
            paymentMethods={availablePaymentMethods}
            pageViewId={pageViewId}
            wegoAnalyticsClientId={wegoAnalyticsClientId}
            wegoAnalyticsClientSessionId={wegoAnalyticsClientSessionId}
            setSelectedPaymentTypes={onPreferredPaymentMethodsChange}
          />
        ) : null}

        {pageType === 'hotels' ? (
          <HotelSearchForm
            geolocationCoordinates={geolocationCoordinates}
            localeParam={localeParam}
            nearestCityByIP={nearestCityByIP}
            wegoAnalyticsClientId={wegoAnalyticsClientId}
            wegoAnalyticsClientSessionId={wegoAnalyticsClientSessionId}
            pageViewId={pageViewId}
          />
        ) : null}
      </div>
    </div>
  );
};

export default SearchForm;
